import { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Heading,
  Button,
  Flex,
  Text,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
  FormControl,
  FormLabel,
  HStack,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  VStack,
  Input,
  IconButton,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
  TagCloseButton,
  Grid,
  GridItem,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Divider
} from "@chakra-ui/react";
import { AnalysisIcon, RefreshIcon, UserIcon } from "../../utils/icons";
import UserDetailedStats from "./UserDetailedStats";
import { 
  FaBalanceScale,
  FaPlus
} from "react-icons/fa";

const AnalysisTab = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [userLevel, setUserLevel] = useState(1);
  const [loadingUsers, setLoadingUsers] = useState(false);
  
  // Estados para comparativo de usuários
  const [searchUser, setSearchUser] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUsersForComparison, setSelectedUsersForComparison] = useState([]);
  const [comparisonPeriod, setComparisonPeriod] = useState('total');
  const [comparisonDataType, setComparisonDataType] = useState('confirmed');
  const [comparisonData, setComparisonData] = useState([]);
  const [loadingComparison, setLoadingComparison] = useState(false);
  
  const toast = useToast();
  const abortControllerRef = useRef(null);
  const usersRef = useRef([]);
  const toastRef = useRef(null);

  // Manter referências atualizadas
  useEffect(() => {
    usersRef.current = users;
  }, [users]);

  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  // Filtrar usuários baseado na busca
  useEffect(() => {
    if (searchUser.trim() === '') {
      setFilteredUsers([]);
    } else {
      const filtered = users.filter(user => 
        !selectedUsersForComparison.find(selected => selected.id === user.id) &&
        ((user.name && user.name.toLowerCase().includes(searchUser.toLowerCase())) ||
         (user.username && user.username.toLowerCase().includes(searchUser.toLowerCase())))
      );
      setFilteredUsers(filtered.slice(0, 10)); // Limitar a 10 resultados
    }
  }, [searchUser, users, selectedUsersForComparison]);

  const fetchUserLevel = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users/me', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar informações do usuário');

      const data = await response.json();
      setUserLevel(data.role);
    } catch (error) {
      console.error('Erro ao buscar nível do usuário:', error);
      setUserLevel(1);
    }
  };

  const fetchUsers = async () => {
    setLoadingUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar usuários');

      const data = await response.json();
      // Incluir todos os usuários (não limitar por nível)
      setUsers(data);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
      toast({
        title: "Erro ao carregar usuários",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserStats = async (userId) => {
    if (!userId) return;
    
    // Cancelar requisição anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Verificar se já está carregando
    if (loading) return;
    
    setLoading(true);
    abortControllerRef.current = new AbortController();
    
    try {
      const token = localStorage.getItem('token');
      
      // CORRIGIDO: Usar URL relativa para evitar problemas de CORS
      const response = await fetch(`/api/users/${userId}/detailed-stats`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      // Buscar informações do usuário selecionado na lista de usuários
      const user = usersRef.current.find(u => u.id === parseInt(userId));
      
      // Só atualizar se a requisição não foi cancelada
      if (!abortControllerRef.current.signal.aborted) {
        setUserStats(data);
        setSelectedUser(user);
      }
      
    } catch (error) {
      // Ignorar erros de abort
      if (error.name === 'AbortError') {
        return;
      }
      
      console.error('Erro ao buscar estatísticas do usuário:', error);
      
      if (toastRef.current) {
        toastRef.current({
          title: "Erro ao carregar estatísticas do usuário",
          description: error.message,
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
      
      // Limpar estados em caso de erro
      setUserStats(null);
      setSelectedUser(null);
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  // Função para buscar dados de comparação
  const fetchComparisonData = async () => {
    if (selectedUsersForComparison.length < 2) {
      toast({
        title: "Usuários insuficientes",
        description: "Selecione pelo menos 2 usuários para comparação",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoadingComparison(true);
    try {
      const token = localStorage.getItem('token');
      const userIds = selectedUsersForComparison.map(user => user.id);
      
      const queryParams = new URLSearchParams({
        userIds: userIds.join(','),
        period: comparisonPeriod,
        dataType: comparisonDataType
      });

      const response = await fetch(`/api/users/comparison-stats?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Erro ao buscar dados de comparação');

      const data = await response.json();
      setComparisonData(data);
    } catch (error) {
      console.error('Erro ao buscar dados de comparação:', error);
      toast({
        title: "Erro ao carregar comparação",
        description: error.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleUserChange = (e) => {
    const userId = e.target.value;
    setSelectedUserId(userId);
    
    // Limpar estados anteriores imediatamente
    setUserStats(null);
    setSelectedUser(null);
    
    if (userId) {
      // Usar setTimeout para evitar múltiplas chamadas rápidas
      setTimeout(() => {
        fetchUserStats(userId);
      }, 100);
    }
  };

  const handleRefresh = () => {
    if (selectedUserId && !loading) {
      fetchUserStats(selectedUserId);
    }
  };

  // Funções para o comparativo
  const addUserToComparison = (user) => {
    if (selectedUsersForComparison.find(selected => selected.id === user.id)) {
      return; // Usuário já selecionado
    }
    
    setSelectedUsersForComparison(prev => [...prev, user]);
    setSearchUser('');
    setFilteredUsers([]);
  };

  const removeUserFromComparison = (userId) => {
    setSelectedUsersForComparison(prev => prev.filter(user => user.id !== userId));
    setComparisonData([]); // Limpar dados de comparação
  };

  const getDataTypeLabel = (dataType) => {
    const labels = {
      confirmed: 'Infrações Confirmadas',
      rejected: 'Infrações Rejeitadas',
      analyzed: 'Infrações Analisadas',
      batches: 'Lotes Concluídos'
    };
    return labels[dataType] || dataType;
  };

  const getPeriodLabel = (period) => {
    const labels = {
      total: 'Total Geral',
      today: 'Hoje',
      yesterday: 'Ontem',
      week: 'Esta Semana',
      month: 'Este Mês',
      '7days': 'Últimos 7 dias',
      '30days': 'Últimos 30 dias'
    };
    return labels[period] || period;
  };

  const getMaxValue = () => {
    if (comparisonData.length === 0) return 0;
    return Math.max(...comparisonData.map(user => user.value || 0));
  };

  useEffect(() => {
    fetchUserLevel();
    fetchUsers();
  }, []);

  // Cleanup na desmontagem do componente
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  if (userLevel < 3) {
    return (
      <Alert status="warning">
        <AlertIcon />
        <Box>
          <AlertTitle>Acesso Restrito!</AlertTitle>
          <AlertDescription>
            Esta seção é disponível apenas para usuários com nível 3 ou superior.
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" display="flex" alignItems="center">
          <AnalysisIcon color="teal.500" />
          Análise Detalhada de Usuários
        </Heading>
        <Button
          size="sm"
          leftIcon={<RefreshIcon />}
          colorScheme="purple"
          onClick={handleRefresh}
          isLoading={loading}
          isDisabled={!selectedUserId}
        >
          Atualizar Análise
        </Button>
      </Flex>

      {/* Seletor de usuário */}
      <Card mb={6}>
        <CardBody>
          <FormControl>
            <FormLabel fontWeight="medium" display="flex" alignItems="center">
              <UserIcon color="blue.500" mr={2} />
              Selecionar Usuário para Análise
            </FormLabel>
            <HStack spacing={4}>
              <Select
                placeholder="Escolha um usuário..."
                value={selectedUserId}
                onChange={handleUserChange}
                isLoading={loadingUsers}
                bg="white"
                maxW="400px"
              >
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.username} ({user.name || 'Nome não informado'}) - Nível {user.role}
                  </option>
                ))}
              </Select>
              
              {selectedUser && (
                <Badge colorScheme="blue" px={3} py={1} borderRadius="full">
                  Nível {selectedUser.role}
                </Badge>
              )}
            </HStack>
          </FormControl>
        </CardBody>
      </Card>

      {/* Estatísticas do usuário selecionado */}
      {loading ? (
        <Flex justify="center" py={10}>
          <VStack spacing={4}>
            <Spinner size="xl" thickness="4px" color="purple.500" />
            <Text color="gray.600">Carregando estatísticas...</Text>
          </VStack>
        </Flex>
      ) : selectedUser && userStats ? (
        <UserDetailedStats 
          user={selectedUser} 
          stats={userStats}
          onRefresh={handleRefresh}
        />
      ) : (
        <Card>
          <CardBody>
            <VStack spacing={4} py={8}>
              <UserIcon color="gray.400" boxSize={12} />
              <Text fontSize="lg" color="gray.500" textAlign="center">
                Selecione um usuário acima para visualizar suas estatísticas detalhadas
              </Text>
              <Text fontSize="sm" color="gray.400" textAlign="center">
                As estatísticas incluem: infrações analisadas, lotes concluídos, taxa de confirmação, histórico de atividades e muito mais.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      )}

      <Divider my={8} />
      
      {/* Nova seção: Comparativo de Usuários */}
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" display="flex" alignItems="center">
          <Box as={FaBalanceScale} color="teal.500" mr={3} />
          Comparativo de Usuários
        </Heading>
        <Button
          size="sm"
          leftIcon={<RefreshIcon />}
          colorScheme="teal"
          onClick={fetchComparisonData}
          isLoading={loadingComparison}
          isDisabled={selectedUsersForComparison.length < 2}
        >
          Gerar Comparação
        </Button>
      </Flex>

      {/* Configurações do comparativo */}
      <Card mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {/* Busca e seleção de usuários */}
            <FormControl>
              <FormLabel fontWeight="medium">
                Adicionar Usuários ({selectedUsersForComparison.length} selecionados)
              </FormLabel>
              <VStack align="stretch" spacing={3}>
                <Input
                  placeholder="Buscar usuário por nome..."
                  value={searchUser}
                  onChange={(e) => setSearchUser(e.target.value)}
                  bg="white"
                />
                
                {/* Lista de usuários filtrados */}
                {filteredUsers.length > 0 && (
                  <Box
                    maxH="150px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    bg="white"
                  >
                    {filteredUsers.map(user => (
                      <Flex
                        key={user.id}
                        p={2}
                        align="center"
                        justify="space-between"
                        _hover={{ bg: "gray.50" }}
                        cursor="pointer"
                        onClick={() => addUserToComparison(user)}
                      >
                        <Text fontSize="sm">
                          {user.name || 'Nome não informado'} (@{user.username})
                        </Text>
                        <IconButton
                          size="xs"
                          icon={<FaPlus />}
                          colorScheme="teal"
                          variant="ghost"
                        />
                      </Flex>
                    ))}
                  </Box>
                )}

                {/* Usuários selecionados */}
                {selectedUsersForComparison.length > 0 && (
                  <Wrap>
                    {selectedUsersForComparison.map(user => (
                      <WrapItem key={user.id}>
                        <Tag
                          size="md"
                          colorScheme="teal"
                          borderRadius="full"
                        >
                          <TagLabel>{user.name || user.username}</TagLabel>
                          <TagCloseButton
                            onClick={() => removeUserFromComparison(user.id)}
                          />
                        </Tag>
                      </WrapItem>
                    ))}
                  </Wrap>
                )}
              </VStack>
            </FormControl>

            {/* Seleção do período */}
            <FormControl>
              <FormLabel fontWeight="medium">Período de Análise</FormLabel>
              <Select
                value={comparisonPeriod}
                onChange={(e) => setComparisonPeriod(e.target.value)}
                bg="white"
              >
                <option value="total">Total Geral</option>
                <option value="today">Hoje</option>
                <option value="yesterday">Ontem</option>
                <option value="week">Esta Semana</option>
                <option value="month">Este Mês</option>
                <option value="7days">Últimos 7 dias</option>
                <option value="30days">Últimos 30 dias</option>
              </Select>
            </FormControl>

            {/* Tipo de dado */}
            <FormControl>
              <FormLabel fontWeight="medium">Tipo de Dado</FormLabel>
              <Select
                value={comparisonDataType}
                onChange={(e) => setComparisonDataType(e.target.value)}
                bg="white"
              >
                <option value="confirmed">Infrações Confirmadas</option>
                <option value="rejected">Infrações Rejeitadas</option>
                <option value="analyzed">Infrações Analisadas</option>
                <option value="batches">Lotes Concluídos</option>
              </Select>
            </FormControl>
          </SimpleGrid>

          {selectedUsersForComparison.length < 2 && (
            <Alert status="info" mt={4}>
              <AlertIcon />
              Adicione pelo menos 2 usuários para realizar a comparação.
            </Alert>
          )}
        </CardBody>
      </Card>

      {/* Gráfico comparativo (barras + média) */}
      {comparisonData.length > 0 && (
        <Box mb={6}>
          <ComparisonChart
            data={comparisonData
              .map(d => ({ ...d }))
              .sort((a, b) => (b.value || 0) - (a.value || 0))}
            height={220}
          />
        </Box>
      )}

      {/* Resultados da comparação */}
      {comparisonData.length > 0 && (
        <Card>
          <CardBody>
            <Heading size="md" mb={4}>
              Comparação: {getDataTypeLabel(comparisonDataType)} - {getPeriodLabel(comparisonPeriod)}
            </Heading>

            {/* Gráfico de barras visual */}
            <VStack spacing={4} mb={6}>
              {comparisonData
                .sort((a, b) => (b.value || 0) - (a.value || 0))
                .map((user, index) => {
                  const percentage = getMaxValue() > 0 ? ((user.value || 0) / getMaxValue()) * 100 : 0;
                  const colors = ['teal', 'blue', 'purple', 'green', 'orange', 'red'];
                  const colorScheme = colors[index % colors.length];
                  
                  return (
                    <Box key={user.userId} w="full">
                      <Flex justify="space-between" align="center" mb={2}>
                        <HStack>
                          <Badge colorScheme={colorScheme} px={2}>
                            #{index + 1}
                          </Badge>
                          <Text fontWeight="medium">{user.userName}</Text>
                        </HStack>
                        <Text fontWeight="bold" color={`${colorScheme}.600`}>
                          {user.value || 0}
                        </Text>
                      </Flex>
                      <Progress
                        value={percentage}
                        colorScheme={colorScheme}
                        size="lg"
                        borderRadius="md"
                      />
                    </Box>
                  );
                })}
            </VStack>

            {/* Tabela detalhada */}
            <TableContainer>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Posição</Th>
                    <Th>Usuário</Th>
                    <Th isNumeric>{getDataTypeLabel(comparisonDataType)}</Th>
                    <Th isNumeric>Percentual do Líder</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {comparisonData
                    .sort((a, b) => (b.value || 0) - (a.value || 0))
                    .map((user, index) => {
                      const percentage = getMaxValue() > 0 ? (((user.value || 0) / getMaxValue()) * 100).toFixed(1) : '0.0';
                      const colors = ['teal', 'blue', 'purple', 'green', 'orange', 'red'];
                      const colorScheme = colors[index % colors.length];
                      
                      return (
                        <Tr key={user.userId}>
                          <Td>
                            <Badge colorScheme={colorScheme}>
                              #{index + 1}
                            </Badge>
                          </Td>
                          <Td fontWeight="medium">{user.userName}</Td>
                          <Td isNumeric>
                            <Text color={`${colorScheme}.600`} fontWeight="bold">
                              {user.value || 0}
                            </Text>
                          </Td>
                          <Td isNumeric>{percentage}%</Td>
                        </Tr>
                      );
                    })}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}
    </Box>
  );
};

const ComparisonChart = ({ data = [], height = 200 }) => {
  const processed = useMemo(() => {
    const items = (data || []).map(d => ({
      id: d.userId ?? d.id ?? (d.user && d.user.id) ?? Math.random().toString(36).slice(2,9),
      // fallback seguro para nome
      name: (d.userName ?? d.name ?? d.username ?? (d.user && d.user.name) ?? '').toString(),
      value: Number(d.value ?? d.total ?? d.count ?? 0) || 0
    }));
    const max = items.length ? Math.max(1, ...items.map(i => i.value)) : 1;
    const avg = items.length > 0 ? items.reduce((s, it) => s + it.value, 0) / items.length : 0;
    return { items, max, avg };
  }, [data]);

  if (!processed.items.length) {
    return (
      <Box p={4} color="gray.500">
        Nenhum dado para exibir no gráfico.
      </Box>
    );
  }

  const padding = 28;
  const gap = 16;
  const barWidth = 48;

  // aumento do espaçamento superior para caber badges/números acima das barras
  const topOffset = 28;

  const chartWidth = Math.max(400, processed.items.length * (barWidth + gap) + padding * 2);
  const chartHeight = height;
  const labelFontSize = 12;

  // ajustar altura total do SVG para acomodar o novo topOffset
  const svgHeight = chartHeight + 40 + (topOffset - 10);
  const avgY = chartHeight - (processed.avg / processed.max) * chartHeight + topOffset;
  const bottomLabelY = chartHeight + (topOffset + 16);

  // palette usada também na tabela/labels (mantém consistência com o resto do componente)
  const colorPalette = [
    { name: 'teal', hex: '#319795' },
    { name: 'blue', hex: '#3182CE' },
    { name: 'purple', hex: '#805AD5' },
    { name: 'green', hex: '#38A169' },
    { name: 'orange', hex: '#ED8936' },
    { name: 'red', hex: '#E53E3E' }
  ];

  return (
    <Box overflowX="auto" w="full" bg="white" borderRadius="md" p={3} boxShadow="sm">
      <Box mb={2} fontSize="sm" color="gray.600">Métrica por usuário</Box>
      <Box minW={`${chartWidth}px`} position="relative">
        <svg width={chartWidth} height={svgHeight} role="img" aria-label="Gráfico comparativo">
          {/* média horizontal */}
          <line
            x1={padding}
            x2={chartWidth - padding}
            y1={avgY}
            y2={avgY}
            stroke="#E53E3E"
            strokeDasharray="6,4"
            strokeWidth="2"
          />
          <text
            x={chartWidth - padding}
            y={avgY - 6}
            fontSize={12}
            textAnchor="end"
            fill="#E53E3E"
          >
            Média: {processed.avg.toFixed(1)}
          </text>

          {/* Barras */}
          {processed.items.map((it, idx) => {
            const x = padding + idx * (barWidth + gap);
            const barH = (it.value / processed.max) * chartHeight;
            const y = chartHeight - barH + topOffset;
            const displayName = (it.name || '').toString();
            const label = displayName.length > 12 ? displayName.slice(0, 12) + '…' : displayName;

            const color = colorPalette[idx % colorPalette.length].hex;

            // badge posicionada acima da barra
            const badgeWidth = 28;
            const badgeHeight = 18;
            const badgeX = x + (barWidth - badgeWidth) / 2;
            const badgeY = Math.max(4, y - badgeHeight - 6);

            return (
              <g key={it.id}>
                {/* barra com cor do usuário */}
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barH}
                  fill={color}
                  rx={4}
                />

                {/* valor acima da barra */}
                <text
                  x={x + barWidth / 2}
                  y={y - 8}
                  fontSize={labelFontSize}
                  textAnchor="middle"
                  fill="#1A202C"
                >
                  {it.value}
                </text>

                {/* label do usuário abaixo */}
                <text
                  x={x + barWidth / 2}
                  y={bottomLabelY}
                  fontSize={labelFontSize}
                  textAnchor="middle"
                  fill="#4A5568"
                >
                  {label}
                </text>
              </g>
            );
          })}
        </svg>
      </Box>
    </Box>
  );
};

export default AnalysisTab;